import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import {
	formatDate,
	formatDateAndTime,
	moneyFormat,
	enumToStringConverter,
} from "../../../helpers/appHelpers";
import { getReimbursementStatusColor } from "../../self_service/reimbursement/data";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface Currency {
	code: string;
}

interface CompanyCurrency {
	currency: Currency;
}

interface ReimbursementRow {
	id: number;
	employee?: Employee;
	title: string;
	description: string;
	amount: number;
	currency?: CompanyCurrency;
	status: "APPROVED" | "SUBMITTED" | "REJECTED" | "QUERIED" | "COMPLETED";
	expenditureDate: string;
	createdAt: string;
	modifiedAt: string;
}

// ========== Reimbursement Columns (HR) ==========
export const reimbursementColumnsHR: ColumnDef<ReimbursementRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="Dp"
						className="w-10 h-10 rounded-full"
					/>
					<p className="truncate">
						{(employee.firstName || "") + " " + (employee.lastName || "")}
					</p>
				</div>
			);
		},
	},
	{
		id: "title",
		accessorKey: "title",
		header: "Title",
		size: 250,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 220,
		cell: ({ row }) => {
			const currencyCode = row.original.currency?.currency?.code || "";
			const amount = row.original.amount;
			return `${currencyCode} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 220,
		cell: ({ row }) => {
			const status = row.original.status as
				| "APPROVED"
				| "SUBMITTED"
				| "REJECTED"
				| "QUERIED"
				| "COMPLETED";
			const color = getReimbursementStatusColor(status);

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: color,
							opacity: "15%",
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		id: "expenditureDate",
		accessorKey: "expenditureDate",
		header: "Expenditure Date",
		size: 220,
		cell: ({ row }) => formatDate(row.original.expenditureDate),
	},
	{
		id: "createdAt",
		accessorKey: "createdAt",
		header: "Date Created",
		size: 220,
		cell: ({ row }) => formatDateAndTime(row.original.createdAt),
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.createdAt).getTime();
			const dateB = new Date(rowB.original.createdAt).getTime();
			return dateA - dateB;
		},
	},
	{
		id: "modifiedAt",
		accessorKey: "modifiedAt",
		header: "Last Date Modified",
		size: 180,
		cell: ({ row }) => formatDateAndTime(row.original.modifiedAt),
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.modifiedAt).getTime();
			const dateB = new Date(rowB.original.modifiedAt).getTime();
			return dateA - dateB;
		},
	},
];

export const reimbursementColumnVisibility: Record<string, boolean> = {
	employee: true,
	title: true,
	description: true,
	amount: true,
	status: true,
	expenditureDate: true,
	createdAt: true,
	modifiedAt: true,
};
